import type {
  ChatMessage,
  ChatReply,
  DayPlan,
  Dossier,
  Lyrics,
  StationSnapshot,
  TasteSummary,
  Track,
} from './types';

(() => {
  try {
    const params = new URLSearchParams(location.search);
    const t = params.get('token');
    if (t) {
      localStorage.setItem('claudio.token', t);
      params.delete('token');
      const qs = params.toString();
      const url = location.pathname + (qs ? `?${qs}` : '') + location.hash;
      history.replaceState(null, '', url);
    }
  } catch {
    /* ignore */
  }
})();

function getToken(): string | null {
  try {
    return localStorage.getItem('claudio.token');
  } catch {
    return null;
  }
}

export function setToken(t: string): void {
  try {
    localStorage.setItem('claudio.token', t);
  } catch {
    /* ignore */
  }
}

async function getJSON<T>(url: string): Promise<T> {
  const token = getToken();
  const r = await fetch(url, token ? { headers: { 'x-claudio-token': token } } : undefined);
  if (!r.ok) throw new Error(`${url} -> ${r.status}`);
  return r.json() as Promise<T>;
}

async function postJSON<T>(url: string, body: unknown): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['x-claudio-token'] = token;
  const r = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`${url} -> ${r.status}`);
  return r.json() as Promise<T>;
}

/** POST that reports the status code instead of throwing — the code route distinguishes 409/413. */
async function postCode(url: string, body: unknown): Promise<{ ok: boolean; status: number }> {
  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['x-claudio-token'] = token;
  try {
    const r = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
    return { ok: r.ok, status: r.status };
  } catch {
    return { ok: false, status: 0 };
  }
}

export type ControlAction = 'play' | 'pause' | 'next' | 'prev' | 'stop' | 'seek' | 'volume' | 'ended';

export const api = {
  login: (username: string, password: string) =>
    postJSON<{ token: string }>('/api/login', { username, password }),
  checkAuth: async (): Promise<boolean> => {
    try {
      const token = getToken();
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 8000); // never hang the gate on a blank screen
      const r = await fetch('/api/now', {
        signal: ctrl.signal,
        headers: token ? { 'x-claudio-token': token } : undefined,
      });
      clearTimeout(t);
      return r.ok;
    } catch {
      return false;
    }
  },
  now: () => getJSON<StationSnapshot>('/api/now'),
  messages: () => getJSON<{ messages: ChatMessage[] }>('/api/messages'),
  search: (q: string) =>
    getJSON<{ results: Array<{ title: string; artist: string; videoId: string; durationSec?: number }> }>(
      `/api/search?q=${encodeURIComponent(q)}`,
    ),
  queue: (items: Array<{ title: string; artist: string }>, replace = false) =>
    postJSON<{ queue: Track[] }>('/api/queue', { items, replace }),
  control: (action: ControlAction, value?: number, uid?: string) =>
    postJSON<StationSnapshot>('/api/control', { action, value, uid }),
  jump: (uid: string) => postJSON<StationSnapshot>('/api/jump', { uid }),
  play: (items: Array<{ title: string; artist: string }>) =>
    postJSON<StationSnapshot>('/api/play', { items }),
  chat: (text: string) => postJSON<ChatReply>('/api/chat', { text }),
  lyrics: (title: string, artist: string, duration?: number) =>
    getJSON<Lyrics>(
      `/api/lyrics?title=${encodeURIComponent(title)}&artist=${encodeURIComponent(artist)}${
        duration ? `&duration=${Math.round(duration)}` : ''
      }`,
    ),
  dossier: (title: string, artist: string, videoId?: string) =>
    getJSON<Dossier>(
      `/api/dossier?title=${encodeURIComponent(title)}&artist=${encodeURIComponent(artist)}${
        videoId ? `&videoId=${encodeURIComponent(videoId)}` : ''
      }`,
    ),
  taste: () => getJSON<TasteSummary>('/api/taste'),
  forgetTaste: (note: string) => postJSON<{ ok: boolean }>('/api/taste/forget', { note }),
  library: () => getJSON<{ playlists: Array<{ name: string; count: number }> }>('/api/library'),
  importLibrary: (url: string) =>
    postJSON<{ platform: string; name: string; count: number }>('/api/library/import', { url }),
  tasteProfile: () => getJSON<{ profile: string }>('/api/library/profile'),
  analyzeLibrary: () => postJSON<{ profile: string }>('/api/library/analyze', {}),
  libraryTracks: (name: string) =>
    getJSON<{ name: string; tracks: Array<{ title: string; artist: string }> }>(
      `/api/library/tracks?name=${encodeURIComponent(name)}`,
    ),
  favorites: () => getJSON<{ tracks: Array<{ title: string; artist: string }> }>('/api/favorites'),
  toggleFavorite: (title: string, artist: string) =>
    postJSON<{ liked: boolean }>('/api/favorites/toggle', { title, artist }),
  planToday: () => getJSON<{ plan: DayPlan | null }>('/api/plan/today'),
  generatePlan: () => postJSON<{ plan: DayPlan | null }>('/api/plan/generate', {}),
  speakers: (scan = true) =>
    getJSON<{ speakers: Array<{ udn: string; name: string }>; active: { udn: string; name: string } | null }>(
      `/api/speakers${scan ? '' : '?scan=0'}`,
    ),
  selectSpeaker: (udn: string | null) =>
    postJSON<{ active: { udn: string; name: string } | null }>('/api/speaker', { udn }),
  capabilities: () =>
    getJSON<{ codeMode: { available: boolean; engine: 'claude' | 'codex'; reason?: string } }>(
      '/api/capabilities',
    ),
  offlineSync: (events: Array<{ uid: string; exit: string; at: number }>, elapsedSec?: number) =>
    postJSON<StationSnapshot & { applied: number }>('/api/offline/sync', { events, elapsedSec }),
  spectrum: (videoId: string) =>
    getJSON<{ fps: number; bands: number; frames: number; data: string }>(
      `/api/spectrum/${encodeURIComponent(videoId)}`,
    ),
  setDriving: (on: boolean) => postJSON<{ driving: boolean }>('/api/driving', { on }),
  reportLocation: (lat: number, lon: number) => postJSON<{ ok: boolean }>('/api/location', { lat, lon }),
  setReading: (on: boolean) => postJSON<{ reading: boolean }>('/api/reading', { on }),
  code: (text: string) => postCode('/api/code', { text }),
  codeStop: () => postCode('/api/code/stop', {}),
  codeReset: () => postCode('/api/code/reset', {}),
};
